// src/components/admin/AdminBookingRequests.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, User, Mail, Phone, CheckCircle, XCircle, AlertCircle, Eye, MessageSquare } from 'lucide-react';
import { getFirestore, collection, query, where, orderBy, onSnapshot, updateDoc, doc, Timestamp, addDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';
import { safeLog, maskEmail, maskPhone, createAuditLog } from '../../utils/gdprUtils';

const AdminBookingRequests = () => {
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const { user } = useAuth();
  const [bookingRequests, setBookingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filter, setFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);
  const [staff, setStaff] = useState([]);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineModal, setShowDeclineModal] = useState(null);

  useEffect(() => {
    if (!currentTenant?.id) return;

    console.log('🔍 AdminBookingRequests: Loading requests for tenant:', currentTenant.id);

    const db = getFirestore();

    // Load staff data for clinician names
    const staffQuery = collection(db, 'tenants', currentTenant.id, 'staff');
    const staffUnsubscribe = onSnapshot(staffQuery, (snapshot) => {
      const staffData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStaff(staffData);
      console.log('✅ AdminBookingRequests: Loaded', staffData.length, 'staff members');
    });

    // Build query based on filter
    let requestsQuery = query(
      collection(db, 'tenants', currentTenant.id, 'appointmentRequests'),
      orderBy('createdAt', 'desc')
    );

    if (filter !== 'all') {
      requestsQuery = query(
        collection(db, 'tenants', currentTenant.id, 'appointmentRequests'),
        where('status', '==', filter),
        orderBy('createdAt', 'desc')
      );
    }

    const requestsUnsubscribe = onSnapshot(requestsQuery, (snapshot) => {
      const requests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setBookingRequests(requests);
      setLoading(false);

      console.log('✅ AdminBookingRequests: Loaded', requests.length, 'booking requests');
      console.log('📊 AdminBookingRequests: Requests:', requests);

      safeLog('Admin booking requests loaded', {
        tenantId: currentTenant.id,
        count: requests.length,
        filter
      });
    }, (error) => {
      console.error('Error loading booking requests:', error);
      setLoading(false);
    });

    return () => {
      staffUnsubscribe();
      requestsUnsubscribe();
    };
  }, [currentTenant?.id, filter]);

  const getClinicianName = (clinicianId) => {
    const clinician = staff.find(s => s.id === clinicianId);
    return clinician ? `${clinician.firstName} ${clinician.lastName}` : clinicianId;
  };

  const handleDecline = (requestId) => {
    setShowDeclineModal(requestId);
    setDeclineReason('');
  };

  const handleDeclineConfirm = async (requestId) => {
    await handleAction(requestId, 'declined', declineReason);
    setShowDeclineModal(null);
    setDeclineReason('');
  };

  const handleAction = async (requestId, action, notes = '') => {
    try {
      setActionLoading(requestId);
      const db = getFirestore();

      // Get the request details for email
      const request = bookingRequests.find(r => r.id === requestId);
      if (!request) {
        throw new Error('Request not found');
      }

      const updateData = {
        status: action,
        [`${action}At`]: Timestamp.now(),
        [`${action}By`]: user?.uid || 'unknown',
        adminNotes: notes || null
      };

      await updateDoc(
        doc(db, 'tenants', currentTenant.id, 'appointmentRequests', requestId),
        updateData
      );

      // Create audit log
      await createAuditLog({
        tenantId: currentTenant.id,
        userId: user?.uid || 'unknown',
        action: `booking_request_${action}`,
        resource: 'appointmentRequest',
        resourceId: requestId,
        details: { notes }
      });

      // Navigate to calendar with pre-filled appointment data if approved
      if (action === 'approved') {
        // Build booking data payload from the approved request
        const bookingData = {
          requestId: requestId,
          date: request.requestedSlot.date,
          time: request.requestedSlot.time,
          duration: request.requestedSlot.duration || 60,
          service: request.serviceId || "Physiotherapy",
          staffId: request.clinicianId,
          notes: request.clientDetails.notes || "",
          client: {
            name: `${request.clientDetails.firstName} ${request.clientDetails.lastName}`,
            email: request.clientDetails.email,
            phone: request.clientDetails.phone,
            dob: request.clientDetails.dateOfBirth || null,
            firstName: request.clientDetails.firstName,
            lastName: request.clientDetails.lastName
          }
        };

        console.log('✅ Navigating to calendar with booking data:', bookingData);

        // Navigate to calendar with booking data in navigation state
        navigate('/calendar', {
          state: {
            openAppointmentModal: true,
            waitlistData: bookingData
          }
        });
      }

      // Send email notification to client
      if (action === 'approved' || action === 'declined') {
        try {
          console.log('🚀 BOOKING DECISION: Starting email notification process');
          console.log('📋 BOOKING DECISION: Action:', action);
          console.log('📋 BOOKING DECISION: Functions instance:', functions);
          console.log('📋 BOOKING DECISION: Functions type:', typeof functions);
          console.log('📋 BOOKING DECISION: Current tenant:', currentTenant);
          console.log('📋 BOOKING DECISION: Request details:', request);
          console.log('📋 BOOKING DECISION: Request ID:', requestId);
          
          if (!functions) {
            console.error('❌ BOOKING DECISION: Firebase Functions not initialized');
            throw new Error('Firebase Functions not initialized');
          }
          
          if (!currentTenant?.id) {
            console.error('❌ BOOKING DECISION: No current tenant available');
            throw new Error('No current tenant available');
          }
          
          if (!request?.clientDetails?.email) {
            console.error('❌ BOOKING DECISION: No client email available');
            console.log('📋 BOOKING DECISION: Client details:', request?.clientDetails);
            throw new Error('No client email available');
          }
          
          const emailData = {
            tenantId: currentTenant.id,
            requestId,
            action,
            clientEmail: request.clientDetails.email,
            declineReason: action === 'declined' ? notes : undefined,
            appointmentId: action === 'approved' ? requestId : undefined
          };
          
          console.log('📧 BOOKING DECISION: Email data payload:', JSON.stringify(emailData, null, 2));
          
          const sendNotification = httpsCallable(functions, 'handleBookingDecision');
          console.log('📞 BOOKING DECISION: Created callable function reference');
          console.log('📞 BOOKING DECISION: About to call Cloud Function with data...');
          
          const startTime = Date.now();
          const result = await sendNotification(emailData);
          const endTime = Date.now();
          
          console.log('✅ BOOKING DECISION: Email sent successfully');
          console.log('📋 BOOKING DECISION: Function execution time:', endTime - startTime, 'ms');
          console.log('📋 BOOKING DECISION: Function result:', result);
          console.log('📋 BOOKING DECISION: Result data:', result.data);
          
          // Show success message to user
          alert(`Email ${action === 'approved' ? 'approval' : 'decline'} notification sent successfully!`);
          
        } catch (emailError) {
          console.error('❌ BOOKING DECISION: Email sending failed:', emailError);
          console.error('📋 BOOKING DECISION: Error code:', emailError.code);
          console.error('📋 BOOKING DECISION: Error message:', emailError.message);
          console.error('📋 BOOKING DECISION: Error details:', emailError.details);
          console.error('📋 BOOKING DECISION: Full error object:', emailError);
          
          // Show error message to user
          alert(`Failed to send email notification: ${emailError.message}`);
          
          // Don't fail the action if email fails - show user notification
          console.warn('⚠️ BOOKING DECISION: Continuing without email notification');
        }
      }

      safeLog(`Booking request ${action}`, {
        tenantId: currentTenant.id,
        requestId,
        action
      });

    } catch (error) {
      console.error(`Error ${action} booking request:`, error);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      declined: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'declined':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Calendar className="w-6 h-6 mr-3 text-blue-600" />
            Booking Requests
          </h2>
          <p className="text-gray-600 mt-1">
            Manage public booking requests from your clinic website
          </p>
        </div>

        {/* Filter buttons */}
        <div className="flex space-x-2">
          {['all', 'pending', 'approved', 'declined'].map(filterOption => (
            <button
              key={filterOption}
              onClick={() => setFilter(filterOption)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter === filterOption
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
              {filterOption !== 'all' && (
                <span className="ml-2 bg-white bg-opacity-20 px-2 py-1 rounded-full text-xs">
                  {bookingRequests.filter(r => r.status === filterOption).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {bookingRequests.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No booking requests
          </h3>
          <p className="text-gray-600">
            {filter === 'all'
              ? 'No booking requests have been submitted yet.'
              : `No ${filter} booking requests found.`
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {bookingRequests.map((request) => (
            <div
              key={request.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    {getStatusIcon(request.status)}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {request.clientDetails?.firstName} {request.clientDetails?.lastName}
                      </h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span className="flex items-center">
                          <Mail className="w-4 h-4 mr-1" />
                          {maskEmail(request.clientDetails?.email)}
                        </span>
                        <span className="flex items-center">
                          <Phone className="w-4 h-4 mr-1" />
                          {maskPhone(request.clientDetails?.phone)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Service</p>
                      <p className="text-sm text-gray-900">{request.serviceId}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Date</p>
                      <p className="text-sm text-gray-900">
                        {formatDate(request.requestedSlot?.start)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Time</p>
                      <p className="text-sm text-gray-900">
                        {formatTime(request.requestedSlot?.start)}
                      </p>
                    </div>
                  </div>

                  {request.clientDetails?.notes && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-1">Client Notes</p>
                      <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                        {request.clientDetails.notes}
                      </p>
                    </div>
                  )}

                  {request.adminNotes && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-1">Admin Notes</p>
                      <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-md">
                        {request.adminNotes}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col items-end space-y-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(request.status)}`}>
                    {request.status}
                  </span>

                  <p className="text-xs text-gray-500">
                    {new Date(request.createdAt).toLocaleDateString('en-GB')}
                  </p>

                  {request.status === 'pending' && (
                    <div className="flex space-x-2 mt-4">
                      <button
                        onClick={() => handleAction(request.id, 'approved')}
                        disabled={actionLoading === request.id}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 text-sm flex items-center"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </button>
                      <button
                        onClick={() => handleDecline(request.id)}
                        disabled={actionLoading === request.id}
                        className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 text-sm flex items-center"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Decline
                      </button>
                    </div>
                  )}

                  <button
                    onClick={() => setSelectedRequest(request)}
                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Request Detail Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  Booking Request Details
                </h3>
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Client Information */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Client Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Name</p>
                      <p className="text-sm text-gray-900">
                        {selectedRequest.clientDetails?.firstName} {selectedRequest.clientDetails?.lastName}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Email</p>
                      <p className="text-sm text-gray-900">{selectedRequest.clientDetails?.email}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Phone</p>
                      <p className="text-sm text-gray-900">{selectedRequest.clientDetails?.phone}</p>
                    </div>
                    {selectedRequest.clientDetails?.dateOfBirth && (
                      <div>
                        <p className="text-sm font-medium text-gray-700">Date of Birth</p>
                        <p className="text-sm text-gray-900">
                          {new Date(selectedRequest.clientDetails.dateOfBirth).toLocaleDateString('en-GB')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Appointment Details */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Appointment Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Service</p>
                      <p className="text-sm text-gray-900">{selectedRequest.serviceId}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Clinician</p>
                      <p className="text-sm text-gray-900">{getClinicianName(selectedRequest.clinicianId)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Date</p>
                      <p className="text-sm text-gray-900">
                        {formatDate(selectedRequest.requestedSlot?.start)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Time</p>
                      <p className="text-sm text-gray-900">
                        {formatTime(selectedRequest.requestedSlot?.start)} - {formatTime(selectedRequest.requestedSlot?.end)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {selectedRequest.clientDetails?.notes && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Client Notes</h4>
                    <p className="text-sm text-gray-600 bg-gray-50 p-4 rounded-md">
                      {selectedRequest.clientDetails.notes}
                    </p>
                  </div>
                )}

                {/* Admin Actions */}
                {selectedRequest.status === 'pending' && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Actions</h4>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => {
                          handleAction(selectedRequest.id, 'approved');
                          setSelectedRequest(null);
                        }}
                        disabled={actionLoading === selectedRequest.id}
                        className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve Request
                      </button>
                      <button
                        onClick={() => handleDecline(selectedRequest.id)}
                        disabled={actionLoading === selectedRequest.id}
                        className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Decline Request
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Decline Reason Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Decline Booking Request</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for declining this booking request. This will be sent to the client.
            </p>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Enter decline reason..."
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              rows={4}
              required
            />
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowDeclineModal(null);
                  setDeclineReason('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeclineConfirm(showDeclineModal)}
                disabled={!declineReason.trim() || actionLoading === showDeclineModal}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading === showDeclineModal ? 'Declining...' : 'Decline Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBookingRequests;
